import React, { useState, useEffect } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const NAV_ITEMS = [
  { to: '/carte',    icon: 'map',          label: 'Carte'    },
  { to: '/messages', icon: 'chat_bubble',  label: 'Messages' },
  { to: '/moment',   icon: 'add_circle',   label: 'Moment'   },
  { to: '/profil',   icon: 'person',       label: 'Profil'   },
];

export default function Navigation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <>
      {/* ===== HEADER ===== */}
      <header style={S.header}>
        <div style={S.inner}>
          <Link to="/" style={S.logo}>Kindeo</Link>

          {/* Nav desktop — cachée sur mobile */}
          {user && !isMobile && (
            <nav style={S.desktopNav}>
              {NAV_ITEMS.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  style={({ isActive }) => ({
                    ...S.desktopLink,
                    ...(isActive ? S.desktopLinkActive : {}),
                  })}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 17 }}>
                    {item.icon}
                  </span>
                  {item.label}
                </NavLink>
              ))}
            </nav>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            {user ? (
              !isMobile && (
                <button
                  onClick={() => { logout(); navigate('/'); }}
                  style={S.logoutBtn}
                >
                  Se déconnecter
                </button>
              )
            ) : (
              <Link to="/inscription" style={S.joinBtn}>
                Rejoindre
              </Link>
            )}

            {/* Hamburger — uniquement mobile et connecté */}
            {user && isMobile && (
              <button
                style={S.hamburger}
                onClick={() => setMenuOpen(o => !o)}
                aria-label="Menu"
              >
                <span style={{ ...S.hLine, ...(menuOpen ? S.h1open : {}) }} />
                <span style={{ ...S.hLine, ...(menuOpen ? S.h2open : {}) }} />
                <span style={{ ...S.hLine, ...(menuOpen ? S.h3open : {}) }} />
              </button>
            )}
          </div>
        </div>

        {/* Menu mobile déroulant */}
        {menuOpen && user && isMobile && (
          <nav style={S.mobileMenu}>
            {NAV_ITEMS.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                style={({ isActive }) => ({
                  ...S.mobileItem,
                  ...(isActive ? S.mobileItemActive : {}),
                })}
                onClick={() => setMenuOpen(false)}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                  {item.icon}
                </span>
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      {/* ===== BOTTOM NAV — mobile uniquement, si connecté ===== */}
      {user && isMobile && (
        <nav style={S.bottomNav}>
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              style={({ isActive }) => ({
                ...S.navItem,
                ...(isActive ? S.navItemActive : {}),
              })}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 24 }}>
                {item.icon}
              </span>
              <span style={S.navLabel}>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </>
  );
}

const S = {
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    background: 'rgba(247,245,241,.92)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    borderBottom: '1px solid var(--c-border)',
    paddingTop: 'env(safe-area-inset-top, 0px)',
  },
  inner: {
    maxWidth: 1100,
    margin: '0 auto',
    height: 64,
    padding: '0 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  logo: {
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 800,
    fontSize: 22,
    color: 'var(--c-primary)',
    letterSpacing: '-0.5px',
    flexShrink: 0,
    textDecoration: 'none',
  },
  desktopNav: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginLeft: 'auto',
  },
  desktopLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 14px',
    borderRadius: 'var(--radius-pill)',
    fontFamily: 'Manrope, sans-serif',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--c-text-2)',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  desktopLinkActive: {
    background: 'var(--c-primary-lt)',
    color: 'var(--c-primary)',
    fontWeight: 700,
  },
  joinBtn: {
    fontFamily: 'Manrope, sans-serif',
    fontSize: 13,
    fontWeight: 700,
    background: 'var(--c-primary)',
    color: '#fff',
    padding: '8px 18px',
    borderRadius: 'var(--radius-pill)',
    textDecoration: 'none',
  },
  logoutBtn: {
    fontFamily: 'Manrope, sans-serif',
    fontSize: 12,
    fontWeight: 600,
    background: 'var(--c-surface-2)',
    color: 'var(--c-text-2)',
    border: 'none',
    padding: '7px 14px',
    borderRadius: 'var(--radius-pill)',
    cursor: 'pointer',
  },
  hamburger: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: 24,
    height: 18,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    flexShrink: 0,
  },
  hLine: {
    display: 'block',
    height: 2,
    background: 'var(--c-primary)',
    borderRadius: 2,
    transition: '.2s',
    transformOrigin: 'center',
  },
  h1open: { transform: 'translateY(8px) rotate(45deg)' },
  h2open: { opacity: 0 },
  h3open: { transform: 'translateY(-8px) rotate(-45deg)' },
  mobileMenu: {
    background: 'var(--c-surface)',
    borderBottom: '1px solid var(--c-border)',
    padding: '8px 0',
    boxShadow: 'var(--shadow-md)',
  },
  mobileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 24px',
    fontFamily: 'Manrope, sans-serif',
    fontSize: 15,
    fontWeight: 500,
    color: 'var(--c-text-2)',
    textDecoration: 'none',
  },
  mobileItemActive: {
    color: 'var(--c-primary)',
    fontWeight: 700,
  },
  bottomNav: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    background: 'rgba(247,245,241,.95)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    borderTop: '1px solid var(--c-border)',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    zIndex: 100,
    minHeight: 64,
  },
  navItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    padding: '8px 16px',
    color: 'var(--c-text-3)',
    textDecoration: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  navItemActive: {
    color: 'var(--c-primary)',
  },
  navLabel: {
    fontSize: 10,
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 600,
    letterSpacing: '.3px',
    textTransform: 'uppercase',
  },
};
